import { defineStore } from 'pinia'
import { ref } from 'vue'
import { translate } from '@/i18n'
import type { ApiMessageSeverity } from '@/plugins/axios'

type MessageColor = ApiMessageSeverity

interface QueueMessage {
  color: MessageColor
  text: string
  timeout: number
  icon: string | null
}

interface MessagePayload {
  color: MessageColor
  text: string
  timeout?: number
  icon?: string | null
}

function isMessageColor(value: unknown): value is MessageColor {
  return value === 'success' || value === 'error' || value === 'warning'
}

function messageFromError(exception: unknown, fallback?: string) {
  if (fallback) {
    return fallback
  }

  const notificationMessage = (exception as { notificationMessage?: unknown })?.notificationMessage

  if (typeof notificationMessage === 'string' && notificationMessage) {
    return notificationMessage
  }

  if (exception instanceof Error && exception.message) {
    return exception.message
  }

  return translate('errors.unexpected')
}

function colorFromError(exception: unknown): MessageColor {
  const severity = (exception as { severity?: unknown })?.severity

  return isMessageColor(severity) ? severity : 'error'
}

export const useMessagesStore = defineStore('messages', () => {
  const queue = ref<QueueMessage[]>([])

  function add(message: MessagePayload) {
    queue.value.push({ ...message, timeout: message.timeout ?? 3000, icon: message.icon ?? null })
  }

  function success(text: string, timeout?: number) {
    add({ color: 'success', text, timeout, icon: 'mdi-check-circle' })
  }

  function error(text: string, timeout?: number) {
    add({ color: 'error', text, timeout, icon: 'mdi-alert-circle' })
  }

  function warning(text: string, timeout?: number) {
    add({ color: 'warning', text, timeout, icon: 'mdi-alert' })
  }

  function fromError(exception: unknown, fallback?: string, timeout?: number) {
    add({
      color: colorFromError(exception),
      text: messageFromError(exception, fallback),
      timeout,
    })
  }

  return { queue, add, success, error, warning, fromError }
})
